import { ApiError, apiRequest, createIdempotencyKey, errorMessage, jsonBody } from '../api/client'
import type {
  OfflineRegressionCandidate,
  OfflineSyncResult,
  OfflineWorkoutSnapshot,
  Workout,
  WorkoutItem,
  WorkoutSet,
  WorkoutSetPrescription,
} from '../api/types'

const databaseName = 'repinu-workout-offline-v1'
const databaseVersion = 1
const workoutStoreName = 'workouts'
const actionStoreName = 'actions'
const draftStoreName = 'drafts'

export type OfflineActionStatus = 'Pending' | 'ReconciliationRequired'
export type OfflineActionKind = 'SaveSet' | 'Timer' | 'Finish' | 'Unable' | 'PainStop'

export type QueuedOfflineAction = {
  schemaVersion: 1
  clientActionId: string
  idempotencyKey: string
  sequence: number
  status: OfflineActionStatus
  kind: OfflineActionKind
  workoutId: string
  snapshotId: string
  signature: string
  occurredAt: string
  expectedStateVersion: number
  workoutItemId?: string
  setNumber?: number
  actual?: number
  externalLoadKilograms?: number | null
  bandStrengthOrder?: number | null
  supportHeightCentimeters?: number | null
  rackHoleFromTop?: number | null
  effort?: string
  setStatus?: string
  techniqueClean?: boolean
  note?: string | null
  running?: boolean
  candidateId?: string
  userReason?: string | null
  painReported?: boolean
  assistanceSetup?: string
  entryControl?: string
  exitControl?: string
  reconciliationCode?: string
  reconciliationMessage?: string
  syncErrorCode?: string
  syncErrorMessage?: string
}

export type OfflineWorkoutCache = {
  workoutId: string
  localDate: string
  workout: Workout
  snapshot: OfflineWorkoutSnapshot
  cachedAt: string
}

export type OfflineSetDraft = {
  key: string
  workoutId: string
  workoutItemId: string
  setNumber: number
  prescriptionIdentity: string
  actual: number
  actualLoad: string
  actualBandOrder: number | ''
  actualSupportHeight?: string
  actualRackHole?: number | ''
  effort: string
  techniqueClean: boolean
  unableReason: string
  updatedAt: string
}

export function prescriptionIdentity(slot: WorkoutSetPrescription): string {
  if (slot.revisionId) return `revision:${slot.revisionId}`
  return `slot:${JSON.stringify([
    slot.id,
    slot.stateVersion,
    slot.axis,
    slot.effectiveExerciseId,
    slot.target,
    slot.externalLoadKilograms,
    slot.bandStrengthOrder,
    slot.supportHeightCentimeters ?? null,
    slot.rackHoleFromTop ?? null,
    slot.chainStepOrder,
    slot.progressionChainId ?? null,
    slot.progressionStepId ?? null,
    slot.prescriptionBlockCode,
  ])}`
}
export type OfflineQueueState = {
  pending: number
  reconciliationRequired: boolean
  syncFailed: boolean
  message: string | null
}

export interface OfflineWorkoutStore {
  getCache(workoutId: string): Promise<OfflineWorkoutCache | null>
  getCacheByDate(localDate: string): Promise<OfflineWorkoutCache | null>
  putCache(cache: OfflineWorkoutCache): Promise<void>
  listActions(workoutId: string): Promise<QueuedOfflineAction[]>
  putActionAndCache(action: QueuedOfflineAction, cache: OfflineWorkoutCache): Promise<void>
  updateAction(action: QueuedOfflineAction): Promise<void>
  replaceAction(clientActionId: string, action: QueuedOfflineAction): Promise<void>
  deleteAction(clientActionId: string): Promise<void>
  getDraft(key: string): Promise<OfflineSetDraft | null>
  putDraft(draft: OfflineSetDraft): Promise<void>
  deleteDraft(key: string): Promise<void>
}

export interface OfflineWorkoutTransport {
  send(action: QueuedOfflineAction): Promise<OfflineSyncResult>
}

function requestResult<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result)
    request.onerror = () => reject(request.error ?? new Error('IndexedDB request failed.'))
  })
}

function transactionDone(transaction: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    transaction.oncomplete = () => resolve()
    transaction.onabort = () => reject(transaction.error ?? new Error('IndexedDB transaction aborted.'))
    transaction.onerror = () => reject(transaction.error ?? new Error('IndexedDB transaction failed.'))
  })
}

class VolatileOfflineWorkoutStore implements OfflineWorkoutStore {
  private readonly caches = new Map<string, OfflineWorkoutCache>()
  private readonly actions = new Map<string, QueuedOfflineAction>()
  private readonly drafts = new Map<string, OfflineSetDraft>()

  async getCache(workoutId: string) {
    return structuredClone(this.caches.get(workoutId) ?? null)
  }

  async getCacheByDate(localDate: string) {
    return structuredClone([...this.caches.values()].find((cache) => cache.localDate === localDate) ?? null)
  }

  async putCache(cache: OfflineWorkoutCache) {
    this.caches.set(cache.workoutId, structuredClone(cache))
  }

  async listActions(workoutId: string) {
    return structuredClone([...this.actions.values()]
      .filter((action) => action.workoutId === workoutId)
      .sort((left, right) => left.sequence - right.sequence))
  }

  async putActionAndCache(action: QueuedOfflineAction, cache: OfflineWorkoutCache) {
    this.actions.set(action.clientActionId, structuredClone(action))
    this.caches.set(cache.workoutId, structuredClone(cache))
  }

  async updateAction(action: QueuedOfflineAction) {
    this.actions.set(action.clientActionId, structuredClone(action))
  }

  async replaceAction(clientActionId: string, action: QueuedOfflineAction) {
    this.actions.delete(clientActionId)
    this.actions.set(action.clientActionId, structuredClone(action))
  }

  async deleteAction(clientActionId: string) {
    this.actions.delete(clientActionId)
  }

  async getDraft(key: string) {
    return structuredClone(this.drafts.get(key) ?? null)
  }

  async putDraft(draft: OfflineSetDraft) {
    this.drafts.set(draft.key, structuredClone(draft))
  }

  async deleteDraft(key: string) {
    this.drafts.delete(key)
  }
}

export class IndexedDbOfflineWorkoutStore implements OfflineWorkoutStore {
  private databasePromise: Promise<IDBDatabase> | null = null
  private indexedDbUnavailable = false
  private readonly fallback = new VolatileOfflineWorkoutStore()

  private get useFallback(): boolean {
    return typeof indexedDB === 'undefined' || this.indexedDbUnavailable
  }

  private open(): Promise<IDBDatabase> {
    if (this.databasePromise) return this.databasePromise
    this.databasePromise = new Promise((resolve, reject) => {
      const request = indexedDB.open(databaseName, databaseVersion)
      let settled = false
      const fail = (error: Error) => {
        if (settled) return
        settled = true
        this.indexedDbUnavailable = true
        this.databasePromise = null
        reject(error)
      }
      const timeout = globalThis.setTimeout(
        () => fail(new Error('Offline databáze neodpověděla v bezpečném časovém limitu.')),
        3_000,
      )
      request.onupgradeneeded = () => {
        const database = request.result
        if (!database.objectStoreNames.contains(workoutStoreName)) {
          const workouts = database.createObjectStore(workoutStoreName, { keyPath: 'workoutId' })
          workouts.createIndex('localDate', 'localDate', { unique: true })
        }
        if (!database.objectStoreNames.contains(actionStoreName)) {
          const actions = database.createObjectStore(actionStoreName, { keyPath: 'clientActionId' })
          actions.createIndex('workoutId', 'workoutId')
        }
        if (!database.objectStoreNames.contains(draftStoreName)) {
          database.createObjectStore(draftStoreName, { keyPath: 'key' })
        }
      }
      request.onsuccess = () => {
        globalThis.clearTimeout(timeout)
        if (settled) {
          request.result.close()
          return
        }
        settled = true
        request.result.onversionchange = () => request.result.close()
        resolve(request.result)
      }
      request.onerror = () => {
        globalThis.clearTimeout(timeout)
        fail(request.error ?? new Error('Offline databázi se nepodařilo otevřít.'))
      }
      request.onblocked = () => {
        globalThis.clearTimeout(timeout)
        fail(new Error('Offline databázi blokuje starší otevřená karta.'))
      }
    })
    return this.databasePromise
  }

  async getCache(workoutId: string): Promise<OfflineWorkoutCache | null> {
    if (this.useFallback) return this.fallback.getCache(workoutId)
    const database = await this.open()
    const transaction = database.transaction(workoutStoreName, 'readonly')
    const value = await requestResult(
      transaction.objectStore(workoutStoreName).get(workoutId) as IDBRequest<OfflineWorkoutCache | undefined>,
    )
    await transactionDone(transaction)
    return value ?? null
  }

  async getCacheByDate(localDate: string): Promise<OfflineWorkoutCache | null> {
    if (this.useFallback) return this.fallback.getCacheByDate(localDate)
    const database = await this.open()
    const transaction = database.transaction(workoutStoreName, 'readonly')
    const value = await requestResult(
      transaction.objectStore(workoutStoreName).index('localDate').get(localDate) as IDBRequest<OfflineWorkoutCache | undefined>,
    )
    await transactionDone(transaction)
    return value ?? null
  }

  async putCache(cache: OfflineWorkoutCache): Promise<void> {
    if (this.useFallback) return this.fallback.putCache(cache)
    const database = await this.open()
    const transaction = database.transaction(workoutStoreName, 'readwrite')
    transaction.objectStore(workoutStoreName).put(cache)
    await transactionDone(transaction)
  }

  async listActions(workoutId: string): Promise<QueuedOfflineAction[]> {
    if (this.useFallback) return this.fallback.listActions(workoutId)
    const database = await this.open()
    const transaction = database.transaction(actionStoreName, 'readonly')
    const values = await requestResult(
      transaction.objectStore(actionStoreName).index('workoutId').getAll(workoutId) as IDBRequest<QueuedOfflineAction[]>,
    )
    await transactionDone(transaction)
    return values.sort((left, right) => left.sequence - right.sequence)
  }

  async putActionAndCache(action: QueuedOfflineAction, cache: OfflineWorkoutCache): Promise<void> {
    if (this.useFallback) return this.fallback.putActionAndCache(action, cache)
    const database = await this.open()
    const transaction = database.transaction([actionStoreName, workoutStoreName], 'readwrite')
    transaction.objectStore(actionStoreName).put(action)
    transaction.objectStore(workoutStoreName).put(cache)
    await transactionDone(transaction)
  }

  async updateAction(action: QueuedOfflineAction): Promise<void> {
    if (this.useFallback) return this.fallback.updateAction(action)
    const database = await this.open()
    const transaction = database.transaction(actionStoreName, 'readwrite')
    transaction.objectStore(actionStoreName).put(action)
    await transactionDone(transaction)
  }

  async replaceAction(clientActionId: string, action: QueuedOfflineAction): Promise<void> {
    if (this.useFallback) return this.fallback.replaceAction(clientActionId, action)
    const database = await this.open()
    const transaction = database.transaction(actionStoreName, 'readwrite')
    const actions = transaction.objectStore(actionStoreName)
    actions.delete(clientActionId)
    actions.put(action)
    await transactionDone(transaction)
  }

  async deleteAction(clientActionId: string): Promise<void> {
    if (this.useFallback) return this.fallback.deleteAction(clientActionId)
    const database = await this.open()
    const transaction = database.transaction(actionStoreName, 'readwrite')
    transaction.objectStore(actionStoreName).delete(clientActionId)
    await transactionDone(transaction)
  }

  async getDraft(key: string): Promise<OfflineSetDraft | null> {
    if (this.useFallback) return this.fallback.getDraft(key)
    const database = await this.open()
    const transaction = database.transaction(draftStoreName, 'readonly')
    const value = await requestResult(
      transaction.objectStore(draftStoreName).get(key) as IDBRequest<OfflineSetDraft | undefined>,
    )
    await transactionDone(transaction)
    return value ?? null
  }

  async putDraft(draft: OfflineSetDraft): Promise<void> {
    if (this.useFallback) return this.fallback.putDraft(draft)
    const database = await this.open()
    const transaction = database.transaction(draftStoreName, 'readwrite')
    transaction.objectStore(draftStoreName).put(draft)
    await transactionDone(transaction)
  }

  async deleteDraft(key: string): Promise<void> {
    if (this.useFallback) return this.fallback.deleteDraft(key)
    const database = await this.open()
    const transaction = database.transaction(draftStoreName, 'readwrite')
    transaction.objectStore(draftStoreName).delete(key)
    await transactionDone(transaction)
  }
}

export class ApiOfflineWorkoutTransport implements OfflineWorkoutTransport {
  async send(action: QueuedOfflineAction): Promise<OfflineSyncResult> {
    if (action.kind === 'Unable') {
      return apiRequest<OfflineSyncResult>(
        `/coach/workouts/${action.workoutId}/offline-actions/sync`,
        {
          method: 'POST',
          idempotencyKey: action.idempotencyKey,
          body: jsonBody({
            snapshotId: action.snapshotId,
            candidateId: action.candidateId,
            clientActionId: action.clientActionId,
            expectedStateVersion: action.expectedStateVersion,
            signature: action.signature,
            userReason: action.userReason ?? null,
          }),
        },
      )
    }
    if (!['SaveSet', 'Timer', 'Finish', 'PainStop'].includes(action.kind)) {
      throw new Error('Offline akce není na klientském allowlistu.')
    }
    return apiRequest<OfflineSyncResult>(
      `/coach/workouts/${action.workoutId}/offline-actions/sync-action`,
      {
        method: 'POST',
        idempotencyKey: action.idempotencyKey,
        body: jsonBody({
          snapshotId: action.snapshotId,
          clientActionId: action.clientActionId,
          kind: action.kind,
          occurredAt: action.occurredAt,
          expectedStateVersion: action.expectedStateVersion,
          signature: action.signature,
          workoutItemId: action.workoutItemId,
          setNumber: action.setNumber,
          actual: action.actual,
          externalLoadKilograms: action.externalLoadKilograms,
          bandStrengthOrder: action.bandStrengthOrder,
          supportHeightCentimeters: action.supportHeightCentimeters,
          rackHoleFromTop: action.rackHoleFromTop,
          effort: action.effort,
          setStatus: action.setStatus,
          techniqueClean: action.techniqueClean,
          note: action.note,
          running: action.running,
          painReported: action.painReported,
          userReason: action.userReason,
          assistanceSetup: action.assistanceSetup,
          entryControl: action.entryControl,
          exitControl: action.exitControl,
        }),
      },
    )
  }
}

type SaveSetInput = {
  workoutItemId: string
  setNumber: number
  actual: number
  externalLoadKilograms: number | null
  bandStrengthOrder: number | null
  supportHeightCentimeters?: number | null
  rackHoleFromTop?: number | null
  effort: string
  setStatus: string
  techniqueClean: boolean
  note: string | null
}

type PainStopInput = {
  workoutItemId: string
  userReason: string | null
  assistanceSetup: string
  entryControl: string
  exitControl: string
}

export class WorkoutOfflineQueue {
  private syncByWorkout = new Map<string, Promise<OfflineQueueState>>()
  private sequenceOffset = 0
  private readonly store: OfflineWorkoutStore
  private readonly transport: OfflineWorkoutTransport
  private readonly now: () => Date
  private readonly uuid: () => string

  constructor(
    store: OfflineWorkoutStore,
    transport: OfflineWorkoutTransport,
    now: () => Date = () => new Date(),
    uuid: () => string = createIdempotencyKey,
  ) {
    this.store = store
    this.transport = transport
    this.now = now
    this.uuid = uuid
  }

  async cacheOnlineWorkout(workout: Workout, snapshot: OfflineWorkoutSnapshot): Promise<void> {
    if (workout.id !== snapshot.workoutId || !snapshot.signature || !snapshot.payloadHash) {
      throw new Error('Server neposlal platný podepsaný offline předpis.')
    }
    await this.store.putCache({
      workoutId: workout.id,
      localDate: workout.localDate,
      workout: structuredClone(workout),
      snapshot: structuredClone(snapshot),
      cachedAt: this.now().toISOString(),
    })
    this.emitChanged(workout.id)
  }

  async loadCachedWorkoutByDate(localDate: string): Promise<Workout | null> {
    const cache = await this.store.getCacheByDate(localDate)
    if (!cache || Date.parse(cache.snapshot.expiresAt) <= this.now().getTime()) return null
    return structuredClone(cache.workout)
  }

  async getState(workoutId: string): Promise<OfflineQueueState> {
    const actions = await this.store.listActions(workoutId)
    const reconciliation = actions.find((action) => action.status === 'ReconciliationRequired')
    const syncFailure = actions.find((action) => action.status === 'Pending' && action.syncErrorMessage)
    return {
      pending: actions.filter((action) => action.status === 'Pending').length,
      reconciliationRequired: Boolean(reconciliation),
      syncFailed: Boolean(syncFailure),
      message: reconciliation?.reconciliationMessage ?? syncFailure?.syncErrorMessage ?? null,
    }
  }

  async enqueueSaveSet(workoutId: string, input: SaveSetInput): Promise<Workout> {
    const cache = await this.requireCache(workoutId)
    const snapshotItem = cache.snapshot.items.find((item) => item.workoutItemId === input.workoutItemId)
    const signedSlot = snapshotItem?.setSlots.find((slot) => slot.setNumber === input.setNumber && !slot.isConsumed)
    const item = cache.workout.items.find((candidate) => candidate.id === input.workoutItemId)
    const slot = item?.setPrescriptions?.find((candidate) => candidate.setNumber === input.setNumber && !candidate.isConsumed)
    if (!snapshotItem || !signedSlot || !item || !slot || cache.workout.status !== 'InProgress') {
      throw new Error('Tuto sérii nelze bezpečně zapsat offline. Připoj se a obnov trénink.')
    }
    const requiresSupportHeight = signedSlot.supportHeightCentimeters !== null &&
      signedSlot.supportHeightCentimeters !== undefined
    const actualSupportHeight = input.supportHeightCentimeters ?? null
    if (requiresSupportHeight !== (actualSupportHeight !== null)) {
      throw new Error('Skutečná výška opory neodpovídá podepsanému předpisu.')
    }
    const requiresRackHole = signedSlot.rackHoleFromTop !== null &&
      signedSlot.rackHoleFromTop !== undefined
    const actualRackHole = input.rackHoleFromTop ?? null
    if (requiresRackHole !== (actualRackHole !== null)) {
      throw new Error('Skutečná pozice osy neodpovídá podepsanému předpisu.')
    }

    const occurredAt = this.now().toISOString()
    const clientActionId = this.uuid()
    const action = this.action(cache, 'SaveSet', item.stateVersion, occurredAt, {
      clientActionId,
      workoutItemId: item.id,
      setNumber: input.setNumber,
      actual: input.actual,
      externalLoadKilograms: input.externalLoadKilograms,
      bandStrengthOrder: input.bandStrengthOrder,
      supportHeightCentimeters: actualSupportHeight,
      effort: input.effort,
      rackHoleFromTop: actualRackHole,
      setStatus: input.setStatus,
      techniqueClean: input.techniqueClean,
      note: input.note,
    })
    const workout = structuredClone(cache.workout)
    const updatedItem = workout.items.find((candidate) => candidate.id === item.id)!
    const updatedSlot = updatedItem.setPrescriptions!.find((candidate) => candidate.setNumber === input.setNumber)!
    updatedSlot.isConsumed = true
    updatedSlot.stateVersion += 1
    const observation: WorkoutSet = {
      id: `offline-${clientActionId}`,
      setNumber: input.setNumber,
      effectiveExerciseId: signedSlot.effectiveExerciseId,
      prescribedTarget: signedSlot.target,
      prescribedLoadKilograms: signedSlot.externalLoadKilograms,
      prescribedBandStrengthOrder: signedSlot.bandStrengthOrder,
      prescribedSupportHeightCentimeters: signedSlot.supportHeightCentimeters ?? null,
      actual: input.actual,
      prescribedRackHoleFromTop: signedSlot.rackHoleFromTop ?? null,
      externalLoadKilograms: input.externalLoadKilograms,
      bandStrengthOrder: input.bandStrengthOrder,
      supportHeightCentimeters: actualSupportHeight,
      effort: input.effort,
      rackHoleFromTop: actualRackHole,
      status: input.setStatus,
      techniqueClean: input.techniqueClean,
      note: input.note,
      completedAt: occurredAt,
      stateVersion: 0,
    }
    updatedItem.sets = [...updatedItem.sets.filter((set) => set.setNumber !== input.setNumber), observation]
      .sort((left, right) => left.setNumber - right.setNumber)
    updatedItem.status = 'InProgress'
    updatedItem.stateVersion += 1
    workout.stateVersion += 1
    await this.persist(action, cache, workout)
    return workout
  }

  async enqueueTimer(workoutId: string, running: boolean): Promise<Workout> {
    const cache = await this.requireCache(workoutId)
    if (cache.workout.status !== 'InProgress') throw new Error('Nejdřív trénink zahaj na serveru.')
    const occurredAt = this.now().toISOString()
    const action = this.action(cache, 'Timer', cache.workout.stateVersion, occurredAt, { running })
    const workout = structuredClone(cache.workout)
    if (running) {
      if (!workout.timerRunningSince) workout.timerRunningSince = occurredAt
    } else if (workout.timerRunningSince) {
      workout.activeDurationSeconds += elapsedSeconds(workout.timerRunningSince, occurredAt)
      workout.timerRunningSince = null
    }
    workout.stateVersion += 1
    await this.persist(action, cache, workout)
    return workout
  }

  async enqueueFinish(workoutId: string, note: string | null): Promise<Workout> {
    const cache = await this.requireCache(workoutId)
    if (cache.workout.status !== 'InProgress') throw new Error('Nejdřív trénink zahaj na serveru.')
    const occurredAt = this.now().toISOString()
    const action = this.action(cache, 'Finish', cache.workout.stateVersion, occurredAt, { note })
    const workout = structuredClone(cache.workout)
    if (workout.timerRunningSince) {
      workout.activeDurationSeconds += elapsedSeconds(workout.timerRunningSince, occurredAt)
      workout.timerRunningSince = null
    }
    workout.status = 'Completed'
    workout.finishedAt = occurredAt
    workout.note = note
    workout.stateVersion += 1
    await this.persist(action, cache, workout)
    return workout
  }

  async enqueueUnable(
    workoutId: string,
    workoutItemId: string,
    userReason: string | null,
    requestedRegressionExerciseId: string | null = null,
  ): Promise<Workout> {
    const cache = await this.requireCache(workoutId)
    if (cache.workout.status !== 'InProgress') {
      throw new Error('Nejdřív trénink zahaj na serveru.')
    }
    const item = cache.workout.items.find((candidate) => candidate.id === workoutItemId)
    const candidate = cache.snapshot.candidates.find((entry) =>
      entry.workoutItemId === workoutItemId &&
      entry.expectedStateVersion === item?.stateVersion &&
      entry.scope === 'REMAINING_SETS' &&
      (!requestedRegressionExerciseId || entry.after.effectiveExerciseId === requestedRegressionExerciseId))
    if (!item || !candidate) {
      throw new Error('Pro aktuální stav není podepsaná bezpečná offline regrese. Připoj se a obnov trénink.')
    }
    const occurredAt = this.now().toISOString()
    const action = this.action(cache, 'Unable', candidate.expectedStateVersion, occurredAt, {
      workoutItemId,
      candidateId: candidate.candidateId,
      userReason,
    })
    const workout = applySignedCandidate(structuredClone(cache.workout), item, candidate)
    workout.stateVersion += 1
    await this.persist(action, cache, workout)
    return workout
  }

  async enqueuePainStop(workoutId: string, input: PainStopInput): Promise<Workout> {
    const cache = await this.requireCache(workoutId)
    const signedItem = cache.snapshot.items.find((item) => item.workoutItemId === input.workoutItemId)
    const item = cache.workout.items.find((candidate) => candidate.id === input.workoutItemId)
    if (!signedItem || !item || cache.workout.status !== 'InProgress') {
      throw new Error('Bezpečnostní stop nelze z tohoto offline předpisu potvrdit.')
    }
    const occurredAt = this.now().toISOString()
    const action = this.action(cache, 'PainStop', item.stateVersion, occurredAt, {
      workoutItemId: input.workoutItemId,
      painReported: true,
      userReason: input.userReason,
      assistanceSetup: input.assistanceSetup,
      entryControl: input.entryControl,
      exitControl: input.exitControl,
    })
    const workout = structuredClone(cache.workout)
    const updatedItem = workout.items.find((candidate) => candidate.id === input.workoutItemId)!
    updatedItem.status = 'PainStopped'
    updatedItem.stateVersion += 1
    workout.stateVersion += 1
    await this.persist(action, cache, workout)
    return workout
  }

  draftKey(workoutId: string, workoutItemId: string, setNumber: number): string {
    return `${workoutId}:${workoutItemId}:${setNumber}`
  }

  getDraft(key: string): Promise<OfflineSetDraft | null> {
    return this.store.getDraft(key)
  }

  async getCompatibleDraft(key: string, prescriptionIdentity: string): Promise<OfflineSetDraft | null> {
    const draft = await this.store.getDraft(key)
    if (!draft) return null
    if (draft.prescriptionIdentity === prescriptionIdentity) return draft
    await this.store.deleteDraft(key)
    return null
  }

  putDraft(draft: Omit<OfflineSetDraft, 'updatedAt'>): Promise<void> {
    return this.store.putDraft({ ...draft, updatedAt: this.now().toISOString() })
  }

  deleteDraft(key: string): Promise<void> {
    return this.store.deleteDraft(key)
  }

  sync(workoutId: string): Promise<OfflineQueueState> {
    const active = this.syncByWorkout.get(workoutId)
    if (active) return active
    const run = this.syncCore(workoutId).finally(() => this.syncByWorkout.delete(workoutId))
    this.syncByWorkout.set(workoutId, run)
    return run
  }

  private async syncCore(workoutId: string): Promise<OfflineQueueState> {
    const actions = await this.store.listActions(workoutId)
    for (const queuedAction of actions) {
      let action = queuedAction
      if (action.status === 'ReconciliationRequired') {
        if (!canRetryCanonicalZeroLoadAction(action)) break
        const {
          reconciliationCode: _reconciliationCode,
          reconciliationMessage: _reconciliationMessage,
          syncErrorCode: _syncErrorCode,
          syncErrorMessage: _syncErrorMessage,
          ...preserved
        } = action
        const clientActionId = this.uuid()
        action = {
          ...preserved,
          clientActionId,
          idempotencyKey: clientActionId,
          status: 'Pending',
        }
        await this.store.replaceAction(queuedAction.clientActionId, action)
      }
      let result: OfflineSyncResult
      try {
        result = await this.transport.send(action)
      } catch (error) {
        const syncErrorCode = error instanceof ApiError
          ? error.problem.code ?? `http.${error.status}`
          : 'offline.transport_failed'
        const syncErrorMessage = error instanceof ApiError
          ? errorMessage(error)
          : 'Server změny právě nepotvrdil. Zápis zůstal v tomto zařízení a synchronizaci zkusíme znovu.'
        await this.store.updateAction({
          ...action,
          syncErrorCode,
          syncErrorMessage,
        })
        break
      }
      if (result.status === 'ReconciliationRequired') {
        await this.store.updateAction({
          ...action,
          status: 'ReconciliationRequired',
          reconciliationCode: result.code,
          reconciliationMessage: result.message,
        })
        break
      }
      await this.store.deleteAction(action.clientActionId)
      const cache = await this.store.getCache(workoutId)
      if (cache && result.workout) {
        await this.store.putCache({
          ...cache,
          workout: structuredClone(result.workout),
          cachedAt: this.now().toISOString(),
        })
      }
    }
    this.emitChanged(workoutId)
    return this.getState(workoutId)
  }

  private action(
    cache: OfflineWorkoutCache,
    kind: OfflineActionKind,
    expectedStateVersion: number,
    occurredAt: string,
    fields: Partial<QueuedOfflineAction>,
  ): QueuedOfflineAction {
    const clientActionId = fields.clientActionId ?? this.uuid()
    return {
      schemaVersion: 1,
      clientActionId,
      idempotencyKey: clientActionId,
      sequence: this.now().getTime() * 1_000 + (this.sequenceOffset++ % 1_000),
      status: 'Pending',
      kind,
      workoutId: cache.workoutId,
      snapshotId: cache.snapshot.snapshotId,
      signature: cache.snapshot.signature,
      occurredAt,
      expectedStateVersion,
      ...fields,
    }
  }

  private async requireCache(workoutId: string): Promise<OfflineWorkoutCache> {
    const cache = await this.store.getCache(workoutId)
    if (!cache || Date.parse(cache.snapshot.expiresAt) <= this.now().getTime()) {
      throw new Error('Offline předpis chybí nebo expiroval. Připoj se a obnov trénink.')
    }
    return cache
  }

  private async persist(
    action: QueuedOfflineAction,
    cache: OfflineWorkoutCache,
    workout: Workout,
  ): Promise<void> {
    await this.store.putActionAndCache(action, {
      ...cache,
      workout,
      cachedAt: this.now().toISOString(),
    })
    this.emitChanged(cache.workoutId)
  }

  private emitChanged(workoutId: string): void {
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('repinu:offline-queue-change', { detail: { workoutId } }))
    }
  }
}

function canRetryCanonicalZeroLoadAction(action: QueuedOfflineAction): boolean {
  return action.kind === 'SaveSet' &&
    action.reconciliationCode === 'workout.set_modifier_not_supported' &&
    action.externalLoadKilograms === 0 &&
    action.bandStrengthOrder == null
}

function elapsedSeconds(startedAt: string, endedAt: string): number {
  return Math.max(0, Math.floor((Date.parse(endedAt) - Date.parse(startedAt)) / 1_000))
}

function applySignedCandidate(
  workout: Workout,
  previousItem: WorkoutItem,
  candidate: OfflineRegressionCandidate,
): Workout {
  const item = workout.items.find((entry) => entry.id === previousItem.id)!
  const relation = previousItem.regressions.find((entry) => entry.exerciseId === candidate.after.effectiveExerciseId)
  item.effectiveExerciseId = candidate.after.effectiveExerciseId
  if (relation) {
    item.exerciseSlug = relation.slug
    item.exerciseName = relation.name
  }
  item.prescribedTargets = [...candidate.after.prescription.targets]
  item.prescribedLoadKilograms = candidate.after.prescription.externalLoadKilograms
  item.prescribedBandStrengthOrder = candidate.after.prescription.bandStrengthOrder
  item.prescribedChainStepOrder = candidate.after.prescription.chainStepOrder
  item.prescribedProgressionChainId = candidate.after.prescription.progressionChainId ?? null
  item.prescribedProgressionStepId = candidate.after.prescription.progressionStepId ?? null
  item.isTemporarySwap = candidate.after.isTemporarySwap
  item.stateVersion = candidate.after.stateVersion
  if (candidate.after.setPrescriptions?.length) {
    item.setPrescriptions = candidate.after.setPrescriptions.map((slot): WorkoutSetPrescription => ({
      id: slot.id,
      setNumber: slot.setNumber,
      axis: candidate.after.prescription.axis,
      effectiveExerciseId: slot.effectiveExerciseId,
      exerciseSlug: relation?.slug ?? item.exerciseSlug,
      exerciseName: relation?.name ?? item.exerciseName,
      target: slot.target,
      externalLoadKilograms: slot.externalLoadKilograms,
      bandStrengthOrder: slot.bandStrengthOrder,
      chainStepOrder: slot.chainStepOrder,
      progressionChainId: slot.progressionChainId ?? null,
      progressionStepId: slot.progressionStepId ?? null,
      prescriptionBlockCode: slot.prescriptionBlockCode,
      isConsumed: Boolean(slot.consumedAt),
      stateVersion: slot.stateVersion,
    }))
  }
  return workout
}

export const offlineWorkoutQueue = new WorkoutOfflineQueue(
  new IndexedDbOfflineWorkoutStore(),
  new ApiOfflineWorkoutTransport(),
)
